// Core Imports
import { api } from '@/core/lib/api'
import type { TResponse } from '@/core/types/axios'
import type { TAdminListParams } from '@/core/lib/table-query'

// Module Imports
import { avatarEndpoints } from '@/modules/avatar/avatar.constants'
import type {
  TAvatar,
  TCreateAvatarRequest,
  TEditAvatarRequest,
} from '@/modules/avatar/avatar.types'

export async function getAvatarList(
  params: TAdminListParams
): Promise<TResponse<TAvatar[]>> {
  const response = await api.get<TResponse<TAvatar[]>>(
    avatarEndpoints.list(params)
  )

  return response.data
}

export async function getAvatarDetail(id: string): Promise<TAvatar> {
  const response = await api.get<TResponse<TAvatar>>(avatarEndpoints.detail(id))

  return response.data.data
}

// D6 presigned upload (same flow as team logos): ask the API for a short-lived
// PUT URL, upload the file straight to S3 (NOT through `api` — no auth header to
// S3), then return the object key the API finalizes/replaces with.
async function uploadObject(file: File): Promise<string> {
  const contentType = file.type || 'application/octet-stream'
  const presign = await api.post<
    TResponse<{ upload_url: string; key: string }>
  >(avatarEndpoints.presign, { content_type: contentType })
  const { upload_url, key } = presign.data.data

  const put = await fetch(upload_url, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': contentType },
  })
  if (!put.ok) throw new Error(`S3 upload failed (${put.status})`)
  return key
}

export async function createAvatar(
  payload: TCreateAvatarRequest
): Promise<void> {
  const key = await uploadObject(payload.file)
  await api.post<TResponse<TAvatar>>(avatarEndpoints.finalize, {
    name: payload.name,
    key,
  })
}

export async function editAvatar(payload: TEditAvatarRequest): Promise<void> {
  const body: { name?: string; key?: string } = {}
  if (payload.name) body.name = payload.name
  if (payload.file) body.key = await uploadObject(payload.file)

  await api.patch(avatarEndpoints.edit(payload.id), body)
}

export async function deleteAvatar(id: string): Promise<void> {
  await api.delete(avatarEndpoints.delete(id))
}
